import { createSelector } from 'reselect';

import type BusinessRule from '~/types/BusinessRule';

import { type BatchEntryState } from '~/reducers/batch-entry';
import {
  getBatchEntryIdFromProps,
  getBatchEntryIdsFromProps,
  getOrderObjectIdFromProps,
  getOrderObjectIdsFromProps,
} from '~/selectors/props.selectors';
import { type ApplicationState } from '~/store';
import { type OrderSelection } from '~/types/Order';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { BatchEntryStatus } from '~/types/OrdersBatch';

const getBatchEntryStore = (state: ApplicationState) => state.batchEntry;

export const getBatchEntryData = createSelector(
  getBatchEntryStore,
  (batchEntryStore) => batchEntryStore.data,
);

const getBatchEntryAssociatedRules = createSelector(
  getBatchEntryStore,
  (batchEntryStore) => batchEntryStore.associatedRules,
);

export const getBatchEntriesByOrderId = createSelector(
  getBatchEntryStore,
  (batchEntryStore): HashMap<string, number[]> => batchEntryStore.byOrderId,
);

export const getBatchEntriesByOrderObjectIds = createSelector(
  getBatchEntryStore,
  getOrderObjectIdsFromProps,
  (batchEntryStore, orderObjectIds): BatchEntryExtended[] =>
    orderObjectIds.reduce((batchEntries: BatchEntryExtended[], orderObjectId) => {
      const batchEntryIds = batchEntryStore.byOrderId[orderObjectId] || [];

      const outboundBatchEntryIds = batchEntryIds.filter(
        (batchEntryId) =>
          !!batchEntryStore &&
          !!batchEntryStore.data &&
          !!batchEntryStore.data[batchEntryId] &&
          !batchEntryStore.data[batchEntryId].isReturn,
      );

      if (outboundBatchEntryIds.length === 0) return batchEntries; // exclude return batch entries from the list (WEB-1018)

      return batchEntries.concat(
        outboundBatchEntryIds.map((id) => batchEntryStore.data[id]).filter((be) => be),
      );
    }, []),
);

export const getBatchEntryLoadingStates = createSelector(
  getBatchEntryStore,
  (batchEntryStore) => batchEntryStore.loadState,
);

export const getSmartRecAcceptLoadingIds = createSelector(
  getBatchEntryStore,
  (batchEntryStore) => batchEntryStore.smartRecAcceptLoadingIds ?? [],
);

export const getIsBatchEntryLoading = createSelector(
  getBatchEntryLoadingStates,
  getOrderObjectIdFromProps,
  (loadingStates, orderObjectId): boolean => loadingStates[orderObjectId] || false,
);

export const getBatchEntriesIdByOrderObjectId = createSelector(
  getBatchEntryStore,
  getOrderObjectIdFromProps,
  (batchEntryStore, orderObjectId): number[] =>
    (batchEntryStore.byOrderId[orderObjectId] &&
      batchEntryStore.byOrderId[orderObjectId]
        .map((beId) => batchEntryStore.data[beId])
        .filter((be) => be)
        .filter(({ isReturn }) => !isReturn) // exclude return batch entries from the list (WEB-1018)
        .sort((a, b) => a.partialFulfillmentRank - b.partialFulfillmentRank)
        .map((be) => be.id)) ||
    [],
);

const getSortedBatchEntriesByBatchEntryIds = (
  batchEntryStore: BatchEntryState,
  batchEntryIds: number[],
) => {
  const batchEntriesWithValidEntries = batchEntryIds
    .map((beId: number) => batchEntryStore.data[beId])
    .filter((be: BatchEntryExtended) => be);

  return batchEntriesWithValidEntries
    .sort((a, b) => a.partialFulfillmentRank - b.partialFulfillmentRank)
    .filter(({ isReturn }) => !isReturn) // exclude return batch entries from the list (WEB-1018)
    .map((be) => be.id);
};
const getBatchEntryIdsByOrderObjectId = (
  batchEntryStore: BatchEntryState,
  orderObjectId: string,
) => (batchEntryStore && batchEntryStore.byOrderId ? batchEntryStore.byOrderId[orderObjectId] : []);

export const getOrderSelectionForOrderObjectIdsForUseSelectorHook = (orderObjectIds: string[]) =>
  createSelector(
    getBatchEntryStore,
    (batchEntryStore): OrderSelection =>
      orderObjectIds.reduce((acc: Record<string, number[]>, orderObjectId: string) => {
        const batchEntryIds = getBatchEntryIdsByOrderObjectId(batchEntryStore, orderObjectId);

        const sortedBatchEntryIds = getSortedBatchEntriesByBatchEntryIds(
          batchEntryStore,
          batchEntryIds || [],
        );
        acc[orderObjectId] = sortedBatchEntryIds;
        return acc;
      }, {}),
  );

export const makeGetBatchEntryById = () =>
  createSelector(
    getBatchEntryData,
    getBatchEntryIdFromProps,
    (batchEntryData, batchEntryId): Maybe<BatchEntryExtended> =>
      batchEntryData[batchEntryId] || null,
  );

export const getBatchEntryById = createSelector(
  getBatchEntryData,
  getBatchEntryIdFromProps,
  (batchEntryData, batchEntryId): Maybe<BatchEntryExtended> => batchEntryData[batchEntryId] || null,
);

export const getBatchEntryByIdsStateless = (
  batchEntryData: HashMap<number, BatchEntryExtended>,
  batchEntryIds: number[],
): BatchEntryExtended[] =>
  batchEntryIds
    .map((batchEntryId) => batchEntryData[batchEntryId])
    .filter((batchEntry) => batchEntry);

export const getBatchEntriesByIds = createSelector(
  getBatchEntryData,
  getBatchEntryIdsFromProps,
  getBatchEntryByIdsStateless,
);

export const batchEntryIsCompleted = (batchEntry?: BatchEntryExtended): boolean =>
  !!batchEntry && batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION;

export const batchEntryIsTerminal = (batchEntry?: BatchEntryExtended): boolean =>
  !!batchEntry &&
  (batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION ||
    batchEntry.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED);

export const makeGetBatchEntryIsCompleted = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(getBatchEntryById, (batchEntry): boolean =>
    batchEntryIsCompleted(batchEntry || undefined),
  );
};

export const getBatchEntryIsCompleted = createSelector(getBatchEntryById, (batchEntry): boolean =>
  batchEntry ? batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION : false,
);

export const makeGetBatchEntryByIds = () =>
  createSelector(
    getBatchEntryData,
    getBatchEntryIdsFromProps,
    (batchEntryData, batchEntryIds): Maybe<BatchEntryExtended[]> =>
      batchEntryIds.length > 0 ? batchEntryIds?.map((id) => batchEntryData[id] || null) : [],
  );

export const batchEntryIsProcessingShipment = (batchEntry?: BatchEntryExtended): boolean =>
  !!batchEntry &&
  !batchEntry.shipmentObjectId &&
  batchEntry.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT;

export const makeGetIsBatchEntryProcessingShipment = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(getBatchEntryById, (batchEntry): boolean =>
    batchEntry
      ? !batchEntry.shipmentObjectId &&
        batchEntry.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT
      : false,
  );
};

export const getIsBatchEntryProcessingShipment = createSelector(
  getBatchEntryById,
  (batchEntry): boolean =>
    batchEntry
      ? !batchEntry.shipmentObjectId &&
        batchEntry.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT
      : false,
);

export const makeGetIsBatchEntriesProcessingShipment = () => {
  const getBatchEntryByIds = makeGetBatchEntryByIds();

  return createSelector(getBatchEntryByIds, (batchEntries): boolean =>
    batchEntries && batchEntries.length > 0
      ? batchEntries
          .map((b) =>
            b
              ? !b.shipmentObjectId && b.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT
              : false,
          )
          .some((b) => b)
      : false,
  );
};

export const makeGetBatchEntriesIsCompleted = () => {
  const getBatchEntryByIds = makeGetBatchEntryByIds();

  return createSelector(getBatchEntryByIds, (batchEntries): boolean =>
    batchEntries && batchEntries.length > 0
      ? batchEntries
          .map((b) => (b ? b.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION : false))
          .some((b) => b)
      : false,
  );
};

export const getBatchEntryHasTransactionRequestFailed = createSelector(
  getBatchEntryById,
  (batchEntry): boolean =>
    batchEntry ? batchEntry.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED : false,
);

export const getOrderObjectIdByBatchEntry = createSelector(
  getBatchEntryStore,
  getBatchEntryIdFromProps,
  (batchEntryStore, batchEntryId): Maybe<string> =>
    batchEntryStore.data[batchEntryId]?.orderObjectId || null,
);

export const getRuleForBatchEntry = createSelector(
  getBatchEntryAssociatedRules,
  getBatchEntryIdFromProps,
  (rules, batchEntryId): Maybe<BusinessRule[]> => (rules && rules[batchEntryId]) || null,
);

export const getIsAnyBatchEntryASplitOrder = createSelector(
  getBatchEntryIdsFromProps,
  getBatchEntryData,
  (batchEntryIds, batchEntryData): boolean =>
    batchEntryIds.some((batchEntryId) => {
      if (!batchEntryId || !batchEntryData || !batchEntryData[batchEntryId]) return false;

      const { partialFulfillmentRank } = batchEntryData[batchEntryId];

      return !!partialFulfillmentRank && partialFulfillmentRank > 0;
    }),
);

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished
const getFirstBatchEntryIdFromOrder = createSelector(
  getBatchEntriesByOrderId,
  getOrderObjectIdFromProps,
  (batchEntriesByOrder, orderObjectId): Maybe<number> => {
    const batchEntries = batchEntriesByOrder[orderObjectId] || [];
    if (batchEntries.length !== 1) return null;

    return batchEntries[0];
  },
);

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished
export const getFirstBatchEntryFromOrder = createSelector(
  getFirstBatchEntryIdFromOrder,
  getBatchEntryData,
  (batchEntryId, batchEntryData): Maybe<BatchEntryExtended> => {
    if (!batchEntryId) return null;

    return batchEntryData[batchEntryId];
  },
);
